import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AddressSearchErrorStateMatcher } from 'src/app/helpers/CustomErrorStateMatchers';
import { AddressMustBeWithinCABAValidator } from 'src/app/helpers/FormValidators';
import { StandardizedValidators } from 'src/app/helpers/StandardizedValidators';
import { eAddressType } from 'src/app/models/eAddressType';
import { GoogleMapsService } from 'src/app/services/google-maps.service';
import { AddressFormFields } from '../../classes/AddressFormFields';

@Component({
	selector: 'app-address-form',
	templateUrl: './address-form.component.html',
	styleUrls: ['./address-form.component.scss']
})
export class AddressFormComponent implements AfterViewInit {

	fg : FormGroup;
	@Output() eeValueChange = new EventEmitter<void>();
	@Input() addressType! : eAddressType;
	@Input() mustBeWithinCABA : boolean = false;
	addressSearchErrorStateMatcher = new AddressSearchErrorStateMatcher();

	//#region Google maps
	@ViewChild("mapDiv") mapDiv! : ElementRef;
	@ViewChild("searchInput") input! : ElementRef;
	googleAutocomplete! : google.maps.places.Autocomplete;
	place! : google.maps.places.PlaceResult;
	map! : google.maps.Map;
	marker! : google.maps.Marker;
	//#endregion

	stateAux : string | null = null;
	postalCodeAux : string | null = null;
	routeAux : string | null = null;
	streetNumberAux : string | null = null;

	// Para el díalogo de pasarse a vendedor (en vista de perfil)
	@Input() matFormFieldAppearance : "outline"|"fill" = "outline"

	constructor(private googleMapsService:GoogleMapsService,
				private cdr:ChangeDetectorRef) {
		this.fg = new FormGroup({
			"search": new FormControl(undefined),
			"state": new FormControl(undefined, [Validators.required]),
			"zip_code": new FormControl(undefined, [Validators.required]),
			"address_street": new FormControl(undefined, [Validators.required]),
			"address_number": new FormControl(undefined, [Validators.required]),
			"address_floor": new FormControl(undefined, StandardizedValidators.addressFloor),
			"address_apartment": new FormControl(undefined, StandardizedValidators.addressFloor),
			"extra_notes": new FormControl(undefined, [Validators.maxLength(200)]),
		});

		this.fcState.disable();
		this.fcAddressStreet.disable();
		this.fcAddressNumber.disable();
		
		setTimeout(() => {
			if (this.mustBeWithinCABA) {
				this.fg.addValidators(AddressMustBeWithinCABAValidator);
			}
		}, 0);

		this.fg.valueChanges.subscribe(()=> {
			this.eeValueChange.emit();
		});
	}

	ngAfterViewInit(): void {
		this.googleAutocomplete = this.googleMapsService.constructor_autocomplete(
			this.input.nativeElement,
			() => {
				this.place = this.googleAutocomplete.getPlace();
				if (this.place && this.place.address_components) {
					if (this.validateGooglePlace()) {
						this.fg.setErrors({InvalidPlace: false});
					} else {
						setTimeout(() => {
							this.fg.setErrors({InvalidPlace: true});
						}, 0);
					}
					this.googleMapsService.showPlaceOnMap(this.map, this.place, this.marker);
					this.fillFields();
					this.cdr.detectChanges();
				}
			}
		);
		
		this.map = this.googleMapsService.constructor_map(this.mapDiv.nativeElement);

		this.marker = new google.maps.Marker({
			map: this.map,
			icon: "/assets/img/icons/map_marker.png",
			animation: google.maps.Animation.DROP,
		});
	}

	//#region Getters
	get fcSearch() { return this.fg.get("search")! }
	get fcState() { return this.fg.get("state")! }
	get fcZipCode() { return this.fg.get("zip_code")! }
	get fcAddressStreet() { return this.fg.get("address_street")! }
	get fcAddressNumber() { return this.fg.get("address_number")! }
	get fcAddressFloor() { return this.fg.get("address_floor")! }
	get fcAddressApartment() { return this.fg.get("address_apartment")! }
	get fcExtraNotes() { return this.fg.get("extra_notes")! }
	//#region

	private fillFields() {
		this.fcState.setValue(this.stateAux);
		this.fcZipCode.setValue(this.postalCodeAux);
		this.fcAddressStreet.setValue(this.routeAux);
		this.fcAddressNumber.setValue(this.streetNumberAux);
		this.fg.updateValueAndValidity();
	}

	validateGooglePlace() : boolean {
		let administrative_area_level_1 = this.place.address_components!.find(ac=> ac.types.includes("administrative_area_level_1"));
		this.stateAux = administrative_area_level_1 ? administrative_area_level_1.short_name : null;

		let postal_code = this.place.address_components!.find(ac=> ac.types.includes("postal_code"));
		let postal_code_suffix = this.place.address_components!.find(ac=> ac.types.includes("postal_code_suffix"));
		if (postal_code) {
			this.postalCodeAux = postal_code.long_name
			if (postal_code_suffix) {
				this.postalCodeAux += postal_code_suffix.long_name
			}
		} else {
			this.postalCodeAux = null;
		}

		let route = this.place.address_components!.find(ac=> ac.types.includes("route"));
		this.routeAux = route ? route.long_name : null;
		
		let street_number = this.place.address_components!.find(ac=> ac.types.includes("street_number"));
		this.streetNumberAux = street_number ? street_number.long_name : null;

		return !!(this.place.geometry &&
				this.place.geometry.location &&
				(this.place.types?.includes("street_address") || this.place.types?.includes("establishment") || this.place.types?.includes("premise") || this.place.types?.includes("subpremise")) &&
				this.place.formatted_address &&
				this.stateAux &&
				this.routeAux &&
				this.streetNumberAux);
	}

	toAddressFields() : AddressFormFields {
		return new AddressFormFields(this.addressType, this.fcState.value, this.fcZipCode.value, this.place.formatted_address!,
								this.fcAddressFloor.value, this.fcAddressApartment.value, this.fcExtraNotes.value,
								this.place.geometry!.location!.lat(), this.place.geometry!.location!.lng(), this.place);
	}
}